<template>
  <div class="input-text-component">
    <FormKit
      :id="name.length ? name : undefined"
      v-model="value"
      type="text"
      :name="name.length ? name : undefined"
      :placeholder="placeholder"
      :validation="rules"
      :validation-rules="{ numeric: numericValidation, phone: phoneValidation, zip: zipValidation }"
      :validation-messages="validationMessages"
      validation-visibility="submit"
      :label="props.label"
      :disabled="props.disabled"
      :data-phone-prefix="props.phonePrefix"
      :data-country-id="props.countryId"
      @input="handleInput"
      @blur="handleBlur">
      <template #label="context">
        <label v-if="context.label" class="formkit-label">
          {{ context.label }}
          <span v-if="requiredRef" class="text-sm text-brand-primary ae:text-redDark">*</span>
        </label>
      </template>
    </FormKit>
  </div>
</template>

<script lang="ts" setup>
import type { FormKitNode } from '@formkit/core'
import phoneNumber from '~/utils/formkit/phoneNumber'
import zipCode from '~/utils/formkit/zipCode'
import numericString from '~/utils/formkit/numericString'

const { t } = useLang()

const props = defineProps({
  name: {
    type: String,
    default: '',
    required: false,
  },
  value: {
    type: String,
    default: '',
    required: false,
  },
  label: {
    type: String,
    required: false,
    default: '',
  },
  placeholder: {
    type: String,
    required: true,
  },
  validation: {
    type: String,
    default: '',
    required: false,
  },
  required: {
    type: Boolean,
    default: false,
    required: false,
  },
  minLength: {
    type: Number,
    default: undefined,
    required: false,
  },
  phone: {
    type: Boolean,
    default: false,
    required: false,
  },
  email: {
    type: Boolean,
    default: false,
    required: false,
  },
  phonePrefix: {
    type: String,
    default: '',
    required: false,
  },
  countryId: {
    type: Number,
    default: null,
    required: false,
  },
  disabled: {
    type: Boolean,
    default: false,
    required: false,
  },
})

const requiredRef = ref(false)

const value = ref<string>(props.value)

const rules = computed<string>(() => {
  let rulesArray = []

  if (props.required) {
    rulesArray.push('required')
  }
  if (props.minLength) {
    rulesArray.push('length:' + props.minLength)
  }
  if (props.phone) {
    rulesArray.push('phone')
  }
  if (props.email) {
    rulesArray.push('email')
  }
  if (props.validation?.length) {
    const rawRules = props.validation.split('|')
    rulesArray = rulesArray.concat(rawRules)
  }

  requiredRef.value = rulesArray.includes('required')

  return rulesArray.join('|')
})

const validationMessages = computed(() => ({
  required: t('validation.required'),
  email: t('validation.email'),
  length: (context: { args: Array<any>; name: string; node: FormKitNode }) => {
    if (context.args[1]) {
      return t('validation.length_between', { min: context.args[0], max: context.args[1] })
    }

    return t('validation.min_length', { length: context.args[0] })
  },
  numeric: t('validation.numeric'),
  phone: t('validation.phone'),
  zip: t('validation.zip'),
}))

watch(
  () => props.value,
  (property) => {
    value.value = property
  }
)

const emit = defineEmits(['handle'])

const handleBlur = () => {
  emit('handle', value.value?.trim())
}

function handleInput(value: string) {
  emit('handle', value)
}

const numericValidation = (node: FormKitNode): boolean => numericString(node)
const phoneValidation = (node: FormKitNode): boolean => phoneNumber(node)
const zipValidation = (node: FormKitNode): boolean => zipCode(node)
</script>
